/**
 * Mario Kart World Guide - Main Application Entry Point
 *
 * This app provides a quick reference guide for Mario Kart World tracks and intermissions.
 * Click any track icon on the world map to see race details, shortcuts, and strategies.
 */

import './styles/main.css';
import { initializeData } from './utils/data.js';
import { initializeMap } from './components/map.js';

/**
 * Initialize the application
 */
async function init() {
  console.log('Initializing Mario Kart World Guide...');

  // Show loading state
  showLoadingState();

  // Load track and intermission data
  const dataLoaded = await initializeData();

  if (!dataLoaded) {
    showErrorState('Failed to load track data. Please refresh the page.');
    return;
  }

  // Initialize the interactive map
  initializeMap();

  // Hide loading state
  hideLoadingState();

  // Center the map horizontally on mobile
  if (window.innerWidth <= 768) {
    const main = document.querySelector('main');
    if (main) {
      // Small delay to ensure layout is complete
      setTimeout(() => {
        const maxScrollLeft = main.scrollWidth - main.clientWidth;
        main.scrollLeft = maxScrollLeft / 2;
      }, 100);
    }
  }

  console.log('Mario Kart World Guide initialized successfully!');
}

/**
 * Show loading state
 */
function showLoadingState() {
  const mapContainer = document.getElementById('map-container');
  if (mapContainer) {
    mapContainer.classList.add('opacity-50');
  }
}

/**
 * Hide loading state
 */
function hideLoadingState() {
  const mapContainer = document.getElementById('map-container');
  if (mapContainer) {
    mapContainer.classList.remove('opacity-50');
  }
}

/**
 * Show error state
 * @param {string} message - Error message to display
 */
function showErrorState(message) {
  const app = document.getElementById('app');
  const errorDiv = document.createElement('div');
  errorDiv.className = 'fixed top-20 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-6 py-4 rounded-lg shadow-lg z-50';
  errorDiv.textContent = message;
  app.appendChild(errorDiv);
}

// Subscribe YouTube iframes to state change events on load
document.addEventListener('load', (event) => {
  const el = event.target;
  if (el.tagName === 'IFRAME' && el.src.includes('youtube.com')) {
    el.contentWindow.postMessage(JSON.stringify({ event: 'listening' }), 'https://www.youtube.com');
  }
}, true);

// When a video ends, recreate the iframe so it resets to the correct start time
window.addEventListener('message', (event) => {
  if (event.origin !== 'https://www.youtube.com') return;
  try {
    const data = JSON.parse(event.data);
    if (data.event === 'infoDelivery' && data.info?.playerState === 0) {
      const iframes = document.querySelectorAll('iframe[src*="youtube.com"]');
      for (const iframe of iframes) {
        if (iframe.contentWindow === event.source) {
          const newIframe = iframe.cloneNode(false);
          newIframe.src = iframe.src;
          iframe.parentNode.replaceChild(newIframe, iframe);
          break;
        }
      }
    }
  } catch (e) {
    // ignore non-JSON messages
  }
});

// Start the app when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
